import type { Auth, User } from "firebase/auth";
import { Fragment, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { SourceTag } from "./SourceTag";
import { allPicked, toggled, withAll } from "./bulk";
import { dateLabel } from "./format";
import { go, useTitle } from "./hooks";
import {
  appAreasText,
  areasOf,
  differenceSentences,
  differenceText,
  explainText,
  FLAG_TEXT,
  filterParity,
  flagsText,
  inputRows,
  isOpen,
  NO_PARITY_FILTER,
  NOTE_MAX,
  noteProblem,
  numberText,
  ON_TEXT,
  onePickups,
  PARITY_COLUMNS,
  type ParityFilter,
  parityFilterOf,
  type ParityShow,
  pickupSourceText,
  REASON_TEXT,
  SHOW_TEXT,
  summaryText,
  UNEXPLAINED_NOTE,
  unexplainedText,
  windowText,
  withParityFilter,
} from "./parity";
import { CutoverWatch } from "./CutoverWatch";
import { ParityTrend } from "./ParityHistory";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { type Failure, failureOf, Problem } from "./UserPages";
import { PARITY } from "./store/pageViews";
import { useView, ViewFreshness } from "./store/useView";
import { COMPARE_CASE_TEXT, COMPARE_ROW_COLUMNS, COMPARE_TEXT, COMPARE_WEEK_COLUMNS } from "./visits";
import { Skeleton } from "./Skeleton";

/** Design 26.5 T1: the app and the sheet values of each account of the sheet, and why they differ. */
export function ParityPage({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const view = useView(canView === true ? PARITY.name : null, async () => api.parity(await user.getIdToken()));
  const found: api.Parity | Failure | undefined = view.data ?? (view.error ? failureOf(view.error) : undefined);
  const [filter, setFilter] = useState<ParityFilter>(() => parityFilterOf(location.search));
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [explaining, setExplaining] = useState(false);
  const explainOpen = useRef<HTMLButtonElement>(null);
  const [sort, setSort] = useSort("sort", PARITY_COLUMNS);
  const [opened, setOpened] = useState<Set<string>>(new Set());
  useTitle(`Parity · ${org.name}`);
  useEffect(() => {
    history.replaceState(history.state, "", `${location.pathname}${withParityFilter(location.search, filter)}${location.hash}`);
  }, [filter]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="parity-hidden">
        <h1 tabIndex={-1}>Parity</h1>
        <p>Your role does not see the parity of the app and the sheet.</p>
      </main>
    );
  }
  const page = typeof found === "object" ? found : null;
  const shown = page ? sortRows(filterParity(page.rows, filter), PARITY_COLUMNS, sort) : [];
  const shownIds = shown.map((row) => row.account_id);
  const pickedIds = page ? page.rows.filter((row) => picked.has(row.account_id)).map((row) => row.account_id) : [];
  const change = (next: Partial<ParityFilter>) => setFilter((before) => ({ ...before, ...next }));
  const reload = view.reload;
  const showArea = (area: string) => {
    setFilter({ ...NO_PARITY_FILTER, area, show: "differ" });
    document.getElementById("parity-search")?.scrollIntoView({ block: "start" });
  };
  const showOpen = () => {
    setFilter({ ...NO_PARITY_FILTER, show: "open" });
    console.info("parity: show the rows with no explanation");
    requestAnimationFrame(() => document.getElementById("parity-table")?.scrollIntoView({ block: "start" }));
  };
  return (
    <main className="parity-page wide" data-testid="parity-page">
      <h1 tabIndex={-1}>Parity</h1>
      <p className="sub">
        The shadow run compares the app with the service sheet for each account of the sheet. A row agrees when the rate, the weeks to full and the
        load are each within the tolerance of the gate (10% by default), and the flags are the same. The sheet values come from its gal/wk, its container gallons and its weekly
        pickups, with the rules of the sheet.
      </p>
      <ViewFreshness view={view} timeZone={org.time_zone} auth={auth} />
      {found === undefined ? (
        <Skeleton shape="table" columns={6} rows={8} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={reload} />
      ) : (
        page && (
          <>
            <p className="parity-summary" data-testid="parity-summary" data-gate={page.summary.meets_gate ? "met" : "not-met"}>
              <strong data-testid="parity-share">{summaryText(page.summary)}</strong>{" "}
              {page.summary.unexplained ? (
                <button type="button" className="link error" data-testid="parity-open" onClick={showOpen}>
                  {unexplainedText(page.summary.unexplained)}
                </button>
              ) : (
                <span data-testid="parity-open">{unexplainedText(page.summary.unexplained)}</span>
              )}{" "}
              <span className="sub">
                Cycle from {dateLabel(page.cycle_start)}. The trend and the Cutover page show the count of the last night.
              </span>
              {appAreasText(page.app_areas) && (
                <>
                  {" "}
                  <span className="sub" data-testid="parity-app-areas">
                    {appAreasText(page.app_areas)}
                  </span>
                </>
              )}
            </p>
            <p className="sub" data-testid="parity-sides-note">
              {UNEXPLAINED_NOTE} Open a row with its Result button to see why it differs.
            </p>
            <ParityTrend base={base} auth={auth} user={user} onArea={showArea} />
            <p className="sub" data-testid="parity-ready-link">
              The readiness checks of each area are on the{" "}
              <a href={`${base}/cutover`} onClick={go}>
                Cutover page
              </a>
              .
            </p>
            <CutoverWatch base={base} auth={auth} user={user} />
            {page.rows.length > 0 && (
              <form className="filters" role="search" aria-label="Filter the rows" onSubmit={(e) => e.preventDefault()}>
                <div>
                  <label htmlFor="parity-search">Search</label>
                  <input
                    id="parity-search"
                    type="search"
                    data-testid="parity-search"
                    placeholder="Number or name"
                    value={filter.q}
                    onChange={(e) => change({ q: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="parity-show">Show</label>
                  <select id="parity-show" data-testid="parity-show" value={filter.show} onChange={(e) => change({ show: e.target.value as ParityShow })}>
                    {(Object.keys(SHOW_TEXT) as ParityShow[]).map((value) => (
                      <option key={value} value={value}>
                        {SHOW_TEXT[value]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="parity-area">Area</label>
                  <select id="parity-area" data-testid="parity-area" value={filter.area} onChange={(e) => change({ area: e.target.value })}>
                    <option value="">All</option>
                    {areasOf(page.rows).map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            )}
            {page.can_note && shown.length > 0 && (
              <p className="bulk-bar" data-testid="parity-bulk-bar">
                <label className="check">
                  <input
                    type="checkbox"
                    data-testid="parity-pick-all"
                    checked={allPicked(picked, shownIds)}
                    onChange={(e) => setPicked((before) => withAll(before, shownIds, e.target.checked))}
                  />{" "}
                  Select all shown ({shown.length.toLocaleString("en-US")})
                </label>{" "}
                <span data-testid="parity-picked">{pickedIds.length.toLocaleString("en-US")} selected</span>{" "}
                <button ref={explainOpen} type="button" data-testid="parity-explain-open" disabled={!pickedIds.length} onClick={() => setExplaining(true)}>
                  {explainText(pickedIds.length)}
                </button>
                {pickedIds.length > 0 && (
                  <>
                    {" "}
                    <button type="button" className="quiet" data-testid="parity-clear" onClick={() => setPicked(new Set())}>
                      Clear the selection
                    </button>
                  </>
                )}
              </p>
            )}
            {explaining && pickedIds.length > 0 && (
              <Explain
                user={user}
                ids={pickedIds}
                reasons={page.reasons}
                onClose={() => {
                  setExplaining(false);
                  requestAnimationFrame(() => explainOpen.current?.focus());
                }}
                onSaved={() => {
                  setExplaining(false);
                  setPicked(new Set());
                  reload();
                }}
              />
            )}
            {!page.rows.length ? (
              <p data-testid="parity-empty">No account of the sheet yet. The import adds them.</p>
            ) : !shown.length ? (
              <p data-testid="parity-no-match">No row matches the filters.</p>
            ) : (
              <>
                <SortSelect id="parity-select" columns={PARITY_COLUMNS} sort={sort} onSort={setSort} fallback="Account number" />
                <div id="parity-table" className="table-scroll" tabIndex={0} role="region" aria-label="Parity by account">
                  <table className="parity list" data-testid="parity-rows" data-cols="parity-select">
                    <thead>
                      <tr>
                        {PARITY_COLUMNS.map((column) => (
                          <SortTh
                            key={column.key}
                            column={column}
                            sort={sort}
                            onSort={setSort}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((row) => (
                        <Fragment key={row.account_id}>
                          <tr
                            data-testid="parity-row"
                            data-result={row.agrees ? "agrees" : isOpen(row) ? "open" : "explained"}
                            className={picked.has(row.account_id) ? "picked" : undefined}
                          >
                            <th scope="row">
                              {page.can_note && (
                                <input
                                  type="checkbox"
                                  data-testid="parity-pick"
                                  aria-label={`Select ${row.number}`}
                                  checked={picked.has(row.account_id)}
                                  onChange={() => setPicked((before) => toggled(before, row.account_id))}
                                />
                              )}{" "}
                              <a href={`${base}/accounts/${row.account_id}`} onClick={go}>
                                {row.number}
                              </a>{" "}
                              <span className="sub">{row.name}</span>
                            </th>
                            <td data-label="Area">
                              {row.area ?? "–"}
                              {row.data_source ? (
                                <>
                                  {" "}
                                  <SourceTag tag={row.data_source} />
                                </>
                              ) : (
                                row.on_app && (
                                  <>
                                    {" "}
                                    <span className="chip-flag neutral" data-testid="parity-on-app">
                                      On the app
                                    </span>
                                  </>
                                )
                              )}
                            </td>
                            <td data-label="Result" className={isOpen(row) ? "error" : undefined} data-testid="parity-result">
                              {row.agrees ? (
                                differenceText(row)
                              ) : (
                                <button
                                  type="button"
                                  className="link disclosure"
                                  data-testid="parity-why"
                                  aria-expanded={opened.has(row.account_id)}
                                  aria-controls={`parity-why-${row.account_id}`}
                                  aria-label={`Why ${row.number} differs: ${differenceText(row)}`}
                                  onClick={() => setOpened((before) => toggled(before, row.account_id))}
                                >
                                  {opened.has(row.account_id) ? "▾" : "▸"} {differenceText(row)}
                                </button>
                              )}
                            </td>
                            <Pair label="Rate" sheet={row.sheet.rate} app={row.app.rate} differ={row.differences.includes("rate")} />
                            <Pair label="Weeks to full" sheet={row.sheet.weeks_to_full} app={row.app.weeks_to_full} differ={row.differences.includes("weeks_to_full")} />
                            <Pair label="Load" sheet={row.sheet.load} app={row.app.load} differ={row.differences.includes("load")} />
                            <td data-label="Flags, sheet">{flagsText(row.sheet)}</td>
                            <td data-label="Flags, app" className={row.differences.some((key) => key in FLAG_TEXT) ? "differ" : undefined}>
                              {flagsText(row.app)}
                            </td>
                            <td data-label="Explanation" data-testid="parity-note">
                              {row.note ? (
                                <>
                                  {REASON_TEXT[row.note.reason]}
                                  {row.note.note && <span className="sub"> · {row.note.note}</span>}
                                </>
                              ) : (
                                "–"
                              )}
                            </td>
                          </tr>
                          {opened.has(row.account_id) && !row.agrees && (
                            <tr className="detail-row" id={`parity-why-${row.account_id}`} data-testid="parity-why-row">
                              <td colSpan={PARITY_COLUMNS.length}>
                                <Why user={user} row={row} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="footnote">Rate in gal/wk. Weeks to full is the container gallons ÷ the rate. Load is the gallons at the next visit of this cycle.</p>
              </>
            )}
          </>
        )
      )}
      <SheetAndApp base={base} auth={auth} user={user} />
    </main>
  );
}

/** Design 26.5 T1: one sentence for each difference of a row, then the pickups and the inputs of each side. */
function Why({ user, row }: { user: User; row: api.ParityRow }) {
  const [found, setFound] = useState<api.ParityDetail | "failed" | undefined>(undefined);
  useEffect(() => {
    let live = true;
    const started = performance.now();
    user
      .getIdToken()
      .then((token) => api.parityDetail(token, row.account_id))
      .then((next) => {
        if (!live) return;
        console.info(`parity: the detail of one row, ${next.pickups.length} pickups in ${Math.round(performance.now() - started)} ms`);
        setFound(next);
      })
      .catch((e) => {
        if (!live) return;
        console.info(`parity: the detail of one row failed (${e instanceof api.ApiError ? e.status : "network"})`);
        setFound("failed");
      });
    return () => {
      live = false;
    };
  }, [user, row.account_id]);
  const detail = typeof found === "object" ? found : null;
  const one = detail ? onePickups(detail) : [];
  return (
    <div className="parity-why" data-testid="parity-why-detail" data-state={found === undefined ? "loading" : detail ? "loaded" : "failed"}>
      <ul data-testid="parity-why-sentences">
        {differenceSentences(row, detail).map((sentence) => (
          <li key={sentence}>{sentence}</li>
        ))}
      </ul>
      {found === undefined ? (
        <Skeleton shape="table" columns={4} rows={3} label="Loading the pickups of each side" />
      ) : !detail ? (
        <p className="hint" data-testid="parity-why-failed">
          The pickups of each side did not load. The sentences above come from the row only.
        </p>
      ) : (
        <>
          <p className="sub">
            The window is {windowText(detail.window.start, detail.window.end)}.
            {detail.cut ? " The list shows only the newest pickups." : ""}
          </p>
          {one.length > 0 && (
            <h3 className="parity-why-title" id={`parity-why-one-${row.account_id}`}>
              Pickups that only one side counts
            </h3>
          )}
          {one.length > 0 && (
            <div className="table-scroll" tabIndex={0} role="region" aria-labelledby={`parity-why-one-${row.account_id}`}>
              <table className="list" data-testid="parity-why-pickups">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col" className="num">
                      Gallons
                    </th>
                    <th scope="col">Source</th>
                    <th scope="col">Counted by</th>
                  </tr>
                </thead>
                <tbody>
                  {one.map((pickup) => (
                    <tr key={pickup.id} data-on={pickup.on}>
                      <th scope="row">{dateLabel(pickup.at)}</th>
                      <td className="num" data-label="Gallons">
                        {numberText(pickup.raw_gal)}
                      </td>
                      <td data-label="Source">{pickupSourceText(pickup)}</td>
                      <td data-label="Counted by">{pickup.counted ? ON_TEXT[pickup.on] : `${ON_TEXT[pickup.on]}, before the restart day: not counted`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h3 className="parity-why-title" id={`parity-why-in-${row.account_id}`}>
            Inputs of each side
          </h3>
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby={`parity-why-in-${row.account_id}`}>
            <table className="list" data-testid="parity-why-inputs">
              <thead>
                <tr>
                  <th scope="col">Input</th>
                  <th scope="col">Sheet side</th>
                  <th scope="col">App side</th>
                </tr>
              </thead>
              <tbody>
                {inputRows(detail.inputs).map((input) => (
                  <tr key={input.label} data-differ={input.differ ? "1" : undefined}>
                    <th scope="row">{input.label}</th>
                    <td data-label="Sheet side">{input.sheet}</td>
                    <td data-label="App side" className={input.differ ? "differ" : undefined}>
                      {input.app}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Pair({ label, sheet, app, differ }: { label: string; sheet: number | null; app: number | null; differ: boolean }) {
  return (
    <>
      <td data-label={`${label}, sheet`} className="num">
        {numberText(sheet)}
      </td>
      <td data-label={`${label}, app`} className={differ ? "num differ" : "num"}>
        {numberText(app)}
      </td>
    </>
  );
}

function Explain({
  user,
  ids,
  reasons,
  onClose,
  onSaved,
}: {
  user: User;
  ids: string[];
  reasons: api.ParityReason[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState<api.ParityReason>("timing");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = noteProblem(reason, note);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.parityNotes(await user.getIdToken(), ids, reason, note);
      onSaved();
    } catch (e) {
      setError(e instanceof api.ApiError && e.detail ? e.detail : "Not saved. Try again.");
      setBusy(false);
    }
  };
  return (
    <section className="bulk-edit" aria-labelledby="parity-explain-title" data-testid="parity-explain">
      <h2 id="parity-explain-title">{explainText(ids.length)}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!problem) void save();
        }}
      >
        <p className="sub">One explanation for each selected row, in this cycle. A newer explanation replaces the older one. The audit keeps both.</p>
        <div className="bulk-field">
          <label htmlFor="parity-reason">Reason</label>
          <select id="parity-reason" data-testid="parity-reason" value={reason} onChange={(e) => setReason(e.target.value as api.ParityReason)}>
            {reasons.map((value) => (
              <option key={value} value={value}>
                {REASON_TEXT[value]}
              </option>
            ))}
          </select>
        </div>
        <div className="bulk-field">
          <label htmlFor="parity-note-text">Note{reason === "other" ? "" : " (optional)"}</label>
          <textarea
            id="parity-note-text"
            data-testid="parity-note-text"
            maxLength={NOTE_MAX}
            rows={2}
            value={note}
            aria-invalid={Boolean(problem && note)}
            aria-describedby={problem ? "parity-note-problem" : undefined}
            onChange={(e) => setNote(e.target.value)}
          />
          {problem && (
            <p id="parity-note-problem" className="error">
              {problem}
            </p>
          )}
        </div>
        {error && (
          <p role="alert" className="error" data-testid="parity-explain-error">
            {error}
          </p>
        )}
        <p className="actions">
          <button type="submit" className="primary" data-testid="parity-explain-save" disabled={busy || Boolean(problem)}>
            {busy ? "Saving…" : "Save the explanation"}
          </button>
          <button type="button" className="quiet" data-testid="parity-explain-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </p>
      </form>
    </section>
  );
}

type Compare = { weeks: api.CompareWeek[]; rows?: api.CompareRow[] | null };

/** The weeks where the app and the sheet both have a pickup, so the office can stop the sheet when they agree. */
export function SheetAndApp({ base, auth, user }: { base: string; auth: Auth; user: User }) {
  const [found, setFound] = useState<Compare | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [weekSort, setWeekSort] = useSort("weeks", COMPARE_WEEK_COLUMNS);
  const [rowSort, setRowSort] = useSort("rows", COMPARE_ROW_COLUMNS);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.sheetCompare(token))
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  if (found === "hidden") return null;
  return (
    <section aria-labelledby="sheet-app-title" data-testid="sheet-app">
      <h2 id="sheet-app-title">Sheet and app</h2>
      <p className="sub">For each week: the sheet pickups that a pickup in the app replaced, and the visits that are only in one of the two.</p>
      {found === undefined ? (
        <Skeleton shape="table" columns={4} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : !found.weeks.length ? (
        <p data-testid="sheet-app-empty">No weeks to compare yet.</p>
      ) : (
        <>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Sheet and app by week">
            <table className="list" data-testid="sheet-app-weeks">
              <thead>
                <tr>
                  {COMPARE_WEEK_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={weekSort} onSort={setWeekSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(found.weeks, COMPARE_WEEK_COLUMNS, weekSort).map((week) => (
                  <tr key={week.week_end} data-testid="sheet-app-week">
                    <th scope="row">{week.week_end}</th>
                    {(Object.keys(COMPARE_TEXT) as (keyof typeof COMPARE_TEXT)[]).map((key) => (
                      <td key={key} data-label={COMPARE_TEXT[key]} className="num">
                        {week[key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {found.rows && (
            <>
              <h3>Accounts</h3>
              {!found.rows.length ? (
                <p data-testid="sheet-app-no-rows">No account differs.</p>
              ) : (
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Sheet and app by account">
                  <table className="list" data-testid="sheet-app-rows">
                    <thead>
                      <tr>
                        {COMPARE_ROW_COLUMNS.map((column) => (
                          <SortTh key={column.key} column={column} sort={rowSort} onSort={setRowSort} />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(found.rows, COMPARE_ROW_COLUMNS, rowSort).map((row) => (
                        <tr key={`${row.account_id}-${row.week_end}-${row.case}`} data-testid="sheet-app-row" data-case={row.case}>
                          <th scope="row">
                            <a href={`${base}/accounts/${row.account_id}`} onClick={go}>
                              {row.account_number}
                            </a>{" "}
                            <span className="sub">{row.account_name}</span>
                          </th>
                          <td data-label="Week">{row.week_end}</td>
                          <td data-label="Sheet raw" className="num">
                            {row.sheet_raw ?? "–"}
                          </td>
                          <td data-label="App raw" className="num">
                            {row.app_raw ?? "–"}
                          </td>
                          <td data-label="Case">{COMPARE_CASE_TEXT[row.case] ?? row.case.replace(/_/g, " ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
